import { Box, InputAdornment, Typography } from "@mui/material"
import type { FC } from "react"
import type { FieldArrayWithId } from "react-hook-form"
import { useFormContext } from "react-hook-form"

import { PALLETS_COUNT_OPTIONS } from "../../constants"
import type { AddPalletsAdjustmentFormValues } from "../../schemas"

import { AddPalletsAdjustmentLotFieldSet } from "./AddPalletsAdjustmentLotFieldSet"

import { FormNumericField, FormSelect } from "@/components/UiKit"
import type { Product } from "@/graphql/codegen/graphql"

interface AddPalletsAdjustmentFormPalletsProps {
  product: Product
  uomName: string
  lotFields: FieldArrayWithId<AddPalletsAdjustmentFormValues, "palletRow.lots">[]
  duplicateLotIndexes: Set<number>
  onPalletCountChange: (count: number | null) => void
  onLotAdd: () => void
  onLotRemove: (lotIndex: number) => void
}

export const AddPalletsAdjustmentFormPallets: FC<AddPalletsAdjustmentFormPalletsProps> = ({
  product,
  uomName,
  lotFields,
  duplicateLotIndexes,
  onPalletCountChange,
  onLotAdd,
  onLotRemove,
}) => {
  const { control } = useFormContext<AddPalletsAdjustmentFormValues>()

  const baseFieldPath = "palletRow" as const
  const fieldPaths = {
    quantityPerPallet: `${baseFieldPath}.quantityPerPallet` as const,
    palletCount: `${baseFieldPath}.palletCount` as const,
  }

  return (
    <Box className='flex min-h-0 flex-1 flex-col rounded-lg border border-gray-400'>
      <Typography variant='h6' className='p-4 font-bold leading-10'>
        1. Add New Pallet(s)
      </Typography>
      <Box className='grid min-h-0 flex-1 grid-cols-8 gap-x-4 overflow-y-auto px-4 pb-4'>
        <Box className='col-span-2 flex gap-x-4' data-testid='inventory.add-pallets-adjustment-form-pallets'>
          <FormNumericField
            control={control}
            name={fieldPaths.quantityPerPallet}
            label='Pallet UoM'
            allowNegative={false}
            decimalScale={2}
            className='flex-1'
            slotProps={{ input: { endAdornment: <InputAdornment position='end'>{uomName}</InputAdornment> } }}
            data-testid='inventory.add-pallets-adjustment-form-pallets.pallet-quantity-field'
          />
          <FormSelect
            control={control}
            name={fieldPaths.palletCount}
            label='Pallet Qty'
            required
            options={PALLETS_COUNT_OPTIONS}
            className='flex-1'
            onChange={onPalletCountChange}
            data-testid='inventory.add-pallets-adjustment-form-pallets.pallets-count-field'
          />
        </Box>
        <Box className='col-span-6 flex flex-col gap-y-6'>
          {lotFields.map((lotField, lotIndex) => (
            <AddPalletsAdjustmentLotFieldSet
              key={lotField.id}
              index={lotIndex}
              product={product}
              uomName={uomName}
              isLast={lotIndex === lotFields.length - 1}
              showDelete={lotFields.length > 1}
              isDuplicate={duplicateLotIndexes.has(lotIndex)}
              onAdd={onLotAdd}
              onRemove={() => onLotRemove(lotIndex)}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )
}
